import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { Button } from "reactstrap"
import { FilterData, IndexType } from "components/Extra"
import ProductsTable from "pages/vendor/Products/ProductsTable"
import { ProductCards } from "pages/vendor/Products/ProductsCards"
import { ProductsGrid } from "pages/vendor/Products/ProductsGrid"
import { AddNewProduct } from "pages/vendor/Products/Modals"
import { useNavigate, useSearchParams, useLocation } from "react-router-dom"
import { AddNewOffer } from "pages/vendor/Offers/Modals/AddNewOffer/AddNewOffer"
import { AddNewCampaign } from "pages/vendor/Campaigns/Modals/AddNewCampaign"
import { useTranslation } from "react-i18next"
import { getProducts, deleteProduct, updateProduct } from "../../../store/products/actions"
import { toast } from "react-toastify"

export interface Product {
  id: number
  vsId: number
  enName: string
  arName: string
  enDescription?: string
  arDescription?: string
  description?: string | null
  isActive: boolean
  isCurrent: boolean
  price: number
  categoryId: number
  categoryName: string | null
  categoryEnName?: string
  categoryArName?: string
  brand?: string | null
  internalCode?: string
  stock?: number
  lowStockThreshold?: number
  isBundle?: boolean
  couponGenerationMode?: string
  imageUrl?: string
  unit?: string
  isPinned?: boolean
  pinnedOrder?: number
  specifications?: ProductSpecification[]
  images: ProductImage[]
  totalAvailableQuantity: number
  inventory: ProductInventory[]
  // Supplier info from backend
  supplierId?: number | null
  supplierName?: string | null
  supplierLogo?: string | null
  supplierRating?: number | null
  supplierIsVerified?: boolean | null
  // Product type
  productType?: string | null
}

export interface ProductSpecification {
  specNameEn: string
  specNameAr: string
  specValue: string
  unit: string
  displayOrder: number
  isHighlighted: boolean
}

export interface ProductImage {
  id: number
  productId: number
  url: string
  isMain: boolean
  caption: string | null
  documentId: number
  filePath: string
  isPrimary: boolean
}

export interface ProductInventory {
  terminalId: number
  quantity: number
}

// Simple product options for other modules (Campaigns, Coupons, Offers)
// These should eventually be replaced with Redux store data
export const productOptions = [
  { id: "1", name: "Product A", category: "Water" },
  { id: "2", name: "Product B", category: "Water" },
  { id: "3", name: "Product C", category: "Soft Drinks" },
]

interface ProductsProps {
  isDashboardPage?: boolean
}

export const Products: React.FC<ProductsProps> = ({ isDashboardPage }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  // Get data from Redux
  const { products, loading, pagination } = useSelector(
    (state: any) => state.Products
  )

  const [activeTab, setActiveTab] = useState<IndexType>("list-ul")
  const [checkedProducts, setCheckedProducts] = useState<Product[]>([])
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([])
  const [showAddNewProduct, setShowAddNewProduct] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [openAddNewOfferModal, setOpenAddNewOfferModal] =
    useState<boolean>(false)
  const [openAddNewCompaignModal, setOpenAddNewCompaignModal] =
    useState<boolean>(false)

  const pageSize = 10

  // Get initial page from URL query params (only on mount)
  const getInitialPage = useCallback(() => {
    const params = new URLSearchParams(location.search)
    const pageParam = params.get('page')
    return pageParam ? Math.max(0, parseInt(pageParam) - 1) : 0
  }, [location.search])

  // Load products on mount only - not on location change
  useEffect(() => {
    const initialPage = getInitialPage()
    dispatch(getProducts({
      PageSize: pageSize,
      PageIndex: initialPage,
    }, { push: navigate }))
  }, []) // Empty deps = only run on mount

  // Handle page change for server-side pagination and update URL
  const handlePageChange = useCallback((pageIndex: number) => {
    // Update URL first
    navigate(`${location.pathname}?page=${pageIndex + 1}`, { replace: false })

    // Then fetch products
    dispatch(getProducts({
      PageSize: pageSize,
      PageIndex: pageIndex,
    }, { push: navigate }))
  }, [dispatch, navigate, pageSize, location.pathname])

  // Update filtered products when products change
  useEffect(() => {
    setFilteredProducts(products)
  }, [products])

  const isAllChecked = useMemo(() => {
    return products.length > 0 && checkedProducts.length === products.length
  }, [products, checkedProducts])

  const togglecheckedProductHandler = useCallback((product: Product) => {
    setCheckedProducts(prev =>
      prev.includes(product)
        ? prev.filter(p => p !== product)
        : [...prev, product]
    )
  }, [])

  const toggleAllProducts = useCallback(() => {
    setCheckedProducts(isAllChecked ? [] : products)
  }, [isAllChecked, products])

  const toggleActiveProduct = useCallback(
    (product: Product) => {
      dispatch(updateProduct(
        product.id,
        {
          enName: product.enName,
          arName: product.arName,
          price: product.price,
          categoryId: product.categoryId,
          isActive: !product.isActive,
          availableQuantity: product.totalAvailableQuantity,
        },
        { push: navigate }
      ))
    },
    [dispatch, navigate]
  )

  const addNewProductHandler = useCallback(() => {
    setSelectedProduct(null)
    setShowAddNewProduct(true)
  }, [])

  const editProductHandler = useCallback((product: Product) => {
    setSelectedProduct(product)
    setShowAddNewProduct(true)
  }, [])

  const deleteProductHandler = useCallback((product: Product) => {
    if (window.confirm(t("products_confirmDelete") || "Are you sure you want to delete this product?")) {
      dispatch(deleteProduct(product.id, { push: navigate }))
    }
  }, [dispatch, navigate, t])

  const viewDetailsHandler = useCallback((product: Product) => {
    navigate(`/products/${product.id}`)
  }, [navigate])

  const addNewOffer = useCallback((product: Product) => {
    setOpenAddNewOfferModal(true)
  }, [])

  const addNewCampaign = useCallback((product: Product) => {
    setOpenAddNewCompaignModal(true)
  }, [])

  const duplicateProductHandler = useCallback((product: Product) => {
    // Set the selected product for duplication - the modal will handle creating a copy
    setSelectedProduct(product)
    setShowAddNewProduct(true)
  }, [])

  if (isDashboardPage) {
    return (
      <>
        <FilterData
          data={products}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          bearerFilteredData={setFilteredProducts}
          showFilterBtnInsteadOfToggle
        />

        {/* Modals */}
        <AddNewProduct
          isOpen={showAddNewProduct}
          toggle={() => {
            setShowAddNewProduct(false)
            setSelectedProduct(null)
          }}
          product={selectedProduct}
        />

        <AddNewOffer
          formSubmitedHandler={() => {}}
          isOpen={openAddNewOfferModal}
          toggle={() => setOpenAddNewOfferModal(false)}
        />

        <AddNewCampaign
          isOpen={openAddNewCompaignModal}
          toggle={() => setOpenAddNewCompaignModal(false)}
        />
        <ProductsTable
          products={filteredProducts}
          isAllChecked={isAllChecked}
          checkedProducts={checkedProducts}
          toggleAllProducts={toggleAllProducts}
          togglecheckedProductHandler={togglecheckedProductHandler}
          toggleActiveProduct={toggleActiveProduct}
          editProductHandler={editProductHandler}
          deleteProductHandler={deleteProductHandler}
          viewDetailsHandler={viewDetailsHandler}
        />
      </>
    )
  }

  return (
    <div className="page-content">
      <div className="container-fluid d-flex justify-content-between align-items-center">
        <Breadcrumbs
          title={t("dashboard_dashboard")}
          breadcrumbItem={t("products_products")}
        />
        <Button
          onClick={addNewProductHandler}
          className="bg-gradiant-blue text-capitalize"
        >
          {t("products_addNewProduct")}
        </Button>
      </div>
      <ProductCards />
      <FilterData
        data={products}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        bearerFilteredData={setFilteredProducts}
        showSearchFilterBtn
      />

      {/* Modals */}
      <AddNewProduct
        isOpen={showAddNewProduct}
        toggle={() => {
          setShowAddNewProduct(false)
          setSelectedProduct(null)
        }}
        product={selectedProduct}
      />

      <AddNewOffer
        formSubmitedHandler={() => {}}
        isOpen={openAddNewOfferModal}
        toggle={() => setOpenAddNewOfferModal(false)}
      />

      <AddNewCampaign
        isOpen={openAddNewCompaignModal}
        toggle={() => setOpenAddNewCompaignModal(false)}
      />

      {activeTab == "list-ul" ? (
        <ProductsTable
          products={filteredProducts}
          isAllChecked={isAllChecked}
          checkedProducts={checkedProducts}
          toggleAllProducts={toggleAllProducts}
          togglecheckedProductHandler={togglecheckedProductHandler}
          toggleActiveProduct={toggleActiveProduct}
          editProductHandler={editProductHandler}
          deleteProductHandler={deleteProductHandler}
          viewDetailsHandler={viewDetailsHandler}
          addNewOffer={addNewOffer}
          addNewCampaign={addNewCampaign}
          duplicateProductHandler={duplicateProductHandler}
          pagination={pagination}
          onPageChange={handlePageChange}
        />
      ) : (
        <ProductsGrid
          products={filteredProducts}
          editProductHandler={editProductHandler}
          deleteProductHandler={deleteProductHandler}
          viewDetailsHandler={viewDetailsHandler}
          toggleActiveProduct={toggleActiveProduct}
          addNewOffer={addNewOffer}
          addNewCampaign={addNewCampaign}
          duplicateProductHandler={duplicateProductHandler}
        />
      )}
    </div>
  )
}
